import { useListState } from "@/hooks/use-list-state";
import { useState, useEffect } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import AppLayout from "@/components/app-layout";
import { BetaAccessGate } from "@/components/beta-access-gate";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/searchable-select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Star, RefreshCw, Search, Sparkles, TrendingUp, TrendingDown,
  MessageSquare, Home, User, AlertTriangle, ChevronRight, Filter,
  Flag, ArrowLeft, BarChart2, Settings2, Info, CheckCircle2,
} from "lucide-react";
import type { PadsplitStayReview, PadsplitMoveInReview, PadsplitReviewSummary } from "@shared/schema";
import { usePageTitle } from "@/hooks/use-page-title";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { PendingReviewRequests } from "@/components/reviews/pending-review-requests";

type ReviewWithType = (PadsplitStayReview | PadsplitMoveInReview) & { reviewType: "stay" | "move_in" };
type SentimentFilter = "all" | "positive" | "neutral" | "negative";
type TabType = "all" | "stay" | "move_in" | "pending";
type DateRangeFilter = "all" | "7d" | "30d" | "90d";
type RatingFilter = "all" | "1" | "2" | "3" | "4" | "5";
type MainSection = "reviews" | "manage";

interface FlagRecord {
  id: number;
  title: string;
  message: string;
  createdAt: string | null;
  metadata: {
    reviewId: number;
    reviewType: "stay" | "move_in";
    flaggedAt: string;
    reason: string;
    notes?: string;
  };
}

interface SuppressionRequestRecord {
  id: number;
  userId: number;
  requestedByUserId: number;
  reviewType: "stay" | "move_in";
  reviewId: number;
  padsplitReviewId: string | null;
  memberName: string | null;
  reason: string;
  notes: string | null;
  status: string;
  emailDraftSent: boolean;
  emailSentAt: string | null;
  responseNotes: string | null;
  respondedAt: string | null;
  respondedByUserId: number | null;
  hideReview: boolean;
  createdAt: string;
}

const SUPPRESSION_REASONS: Array<{ value: string; label: string }> = [
  { value: "inaccurate_facts", label: "Inaccurate facts" },
  { value: "retaliation", label: "Retaliatory review" },
  { value: "policy_violation", label: "Policy violation" },
  { value: "other", label: "Other" },
];

function suppressionReasonLabel(value: string): string {
  return SUPPRESSION_REASONS.find(r => r.value === value)?.label ?? value;
}

interface ReviewRequestRecord {
  id: number;
  userId: number;
  memberId: number;
  memberName: string | null;
  status: string;
  notes: string | null;
  requestedAt: string;
}

function dateRangeCutoff(range: DateRangeFilter): Date | null {
  if (range === "all") return null;
  const days = { "7d": 7, "30d": 30, "90d": 90 }[range];
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000);
}

interface RatingTrend {
  month: string;
  count: number;
  avgRating: number | null;
  positive: number;
  negative: number;
  neutral: number;
}

interface Property {
  id: number;
  name: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function StarRating({ rating }: { rating: number | null | undefined }) {
  if (rating == null) return <span className="text-gray-400 text-xs">No rating</span>;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i <= rating ? "fill-amber-400 text-amber-400" : "text-gray-200"}`}
        />
      ))}
      <span className="ml-1 text-xs text-gray-600">{rating}/5</span>
    </div>
  );
}

function SentimentBadge({ sentiment }: { sentiment: string | null | undefined }) {
  if (!sentiment) return null;
  if (sentiment === "positive") return <Badge className="bg-green-100 text-green-700 border-green-200 text-xs" data-testid="badge-sentiment-positive">Positive</Badge>;
  if (sentiment === "negative") return <Badge className="bg-red-100 text-red-700 border-red-200 text-xs" data-testid="badge-sentiment-negative">Negative</Badge>;
  return <Badge className="bg-gray-100 text-gray-600 border-gray-200 text-xs" data-testid="badge-sentiment-neutral">Neutral</Badge>;
}

function ReviewTypeBadge({ type }: { type: "stay" | "move_in" }) {
  return type === "stay"
    ? <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs">Stay Review</Badge>
    : <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-xs">Move-In Review</Badge>;
}

// ── Suppression Request Dialog ────────────────────────────────────────────────

interface FlagTarget {
  reviewId: number;
  reviewType: "stay" | "move_in";
  memberName: string | null;
}

function FlagReviewDialog({
  target,
  onClose,
}: {
  target: FlagTarget | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [reason, setReason] = useState<string>(SUPPRESSION_REASONS[0].value);
  const [notes, setNotes] = useState("");
  const [sendEmailDraft, setSendEmailDraft] = useState(true);

  const submitMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/app/reviews/suppression-requests", {
        reviewId: target!.reviewId,
        reviewType: target!.reviewType,
        reason,
        notes: notes || undefined,
        sendEmailDraft,
      }).then(r => r.json() as Promise<{ success: boolean; emailDelivered: boolean; emailError: string | null }>),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/reviews/suppression-requests"] });
      const description = sendEmailDraft
        ? data.emailDelivered
          ? "A pre-filled email draft was sent to your inbox — forward it to PadSplit support."
          : data.emailError ?? "Saved, but the email draft could not be sent."
        : "Forward your draft to PadSplit support to complete the suppression.";
      toast({
        title: "Suppression request recorded",
        description,
        variant: sendEmailDraft && !data.emailDelivered ? "destructive" : undefined,
      });
      onClose();
      setNotes("");
    },
    onError: () => toast({ title: "Could not record suppression request", variant: "destructive" }),
  });

  if (!target) return null;

  return (
    <Dialog open={!!target} onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-4 w-4 text-red-600" />
            Request Review Suppression
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <p className="text-sm text-gray-600">
            This records a suppression request for{" "}
            <strong>{target.memberName || "this member"}'s</strong>{" "}
            {target.reviewType === "stay" ? "stay" : "move-in"} review. PadSplit
            support reviews suppression requests for inaccurate or retaliatory reviews.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="suppression-reason">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger id="suppression-reason" data-testid="select-suppression-reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPRESSION_REASONS.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="suppression-notes">Additional context (optional)</Label>
            <Textarea
              id="suppression-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Explain why PadSplit should suppress this review…"
              className="h-20 text-sm resize-none"
              data-testid="textarea-suppression-notes"
            />
          </div>
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={sendEmailDraft}
              onChange={e => setSendEmailDraft(e.target.checked)}
              className="mt-0.5"
              data-testid="checkbox-suppression-email-draft"
            />
            <span>
              Email me a pre-filled draft I can forward to PadSplit support.
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="button-suppression-cancel">
            Cancel
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => submitMutation.mutate()}
            disabled={submitMutation.isPending}
            data-testid="button-suppression-submit"
          >
            {submitMutation.isPending ? "Submitting…" : "Request Suppression"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review Card ───────────────────────────────────────────────────────────────

function ReviewCard({
  review,
  onFlag,
  onMemberClick,
  canEdit,
}: {
  review: ReviewWithType;
  onFlag: (t: FlagTarget) => void;
  onMemberClick?: (name: string) => void;
  canEdit: boolean;
}) {
  const date = review.reviewDate ? new Date(review.reviewDate).toLocaleDateString() : null;
  const isBad = (review.rating != null && review.rating < 3) || review.sentiment === "negative";

  return (
    <Card
      className={`border ${isBad ? "border-red-200 bg-red-50/30" : "border-gray-200"}`}
      data-testid={`card-review-${review.id}`}
    >
      <CardContent className="pt-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-2 mb-2">
              <StarRating rating={review.rating} />
              <SentimentBadge sentiment={review.sentiment} />
              <ReviewTypeBadge type={review.reviewType} />
              {isBad && (
                <Badge className="bg-red-100 text-red-700 border-red-200 text-xs flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Low / Negative
                </Badge>
              )}
            </div>

            {review.reviewText ? (
              <p className="text-sm text-gray-800 mb-2 leading-relaxed" data-testid={`text-review-body-${review.id}`}>
                "{review.reviewText}"
              </p>
            ) : (
              <p className="text-sm text-gray-400 italic mb-2">No written review.</p>
            )}

            <div className="flex items-center flex-wrap gap-3 text-xs text-gray-500">
              {review.memberName && (
                onMemberClick ? (
                  <button
                    onClick={() => onMemberClick(review.memberName!)}
                    className="flex items-center gap-1 hover:text-blue-600 hover:underline"
                    data-testid={`text-review-member-${review.id}`}
                    title="View all reviews by this member"
                  >
                    <User className="h-3 w-3" />
                    {review.memberName}
                  </button>
                ) : (
                  <span className="flex items-center gap-1" data-testid={`text-review-member-${review.id}`}>
                    <User className="h-3 w-3" />
                    {review.memberName}
                  </span>
                )
              )}
              {review.memberId && (
                <Link href={`/app/members/${review.memberId}`}>
                  <a className="flex items-center gap-1 text-blue-600 hover:underline">
                    View member <ChevronRight className="h-3 w-3" />
                  </a>
                </Link>
              )}
              {date && (
                <span data-testid={`text-review-date-${review.id}`}>{date}</span>
              )}
            </div>
          </div>

          {canEdit && isBad && (
            <Button
              size="sm"
              variant="ghost"
              className="text-xs text-red-600 hover:bg-red-50 shrink-0 mt-0.5"
              onClick={() => onFlag({ reviewId: review.id, reviewType: review.reviewType, memberName: review.memberName ?? null })}
              data-testid={`button-flag-review-${review.id}`}
            >
              <Flag className="h-3 w-3 mr-1" />
              Request Suppression
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Gemini Summary Panel ──────────────────────────────────────────────────────

function SummaryPanel({ propertyId, propertyName }: { propertyId: number; propertyName: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const summaryQuery = useQuery<PadsplitReviewSummary | null>({
    queryKey: ["/api/app/reviews/summary", propertyId],
    queryFn: async () => {
      const res = await fetch(`/api/app/reviews/summary/${propertyId}`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json() as Promise<PadsplitReviewSummary | null>;
    },
  });

  const refreshMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("POST", `/api/app/reviews/summary/${propertyId}/refresh`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/reviews/summary", propertyId] });
      toast({ title: "AI summary refreshed" });
    },
    onError: () => toast({ title: "Failed to refresh summary", variant: "destructive" }),
  });

  const summary = summaryQuery.data;

  return (
    <Card className="border border-purple-200 bg-purple-50/20">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold text-purple-800 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-600" />
            AI Summary — {propertyName} (30-day window)
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
            data-testid={`button-refresh-summary-${propertyId}`}
            className="text-xs"
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${refreshMutation.isPending ? "animate-spin" : ""}`} />
            {refreshMutation.isPending ? "Generating…" : "Refresh"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {summaryQuery.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : !summary ? (
          <p className="text-sm text-gray-500 italic">
            No AI summary yet. Click Refresh to generate one.
          </p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-800" data-testid={`text-summary-text-${propertyId}`}>{summary.summaryText}</p>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span>{summary.reviewCount} review{summary.reviewCount !== 1 ? "s" : ""} analyzed</span>
              {summary.averageRating && (
                <span className="flex items-center gap-1">
                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                  Avg {parseFloat(summary.averageRating as string).toFixed(1)}
                </span>
              )}
              <span>Updated {new Date(summary.updatedAt).toLocaleDateString()}</span>
            </div>
            {summary.keyThemes && summary.keyThemes.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {(summary.keyThemes as string[]).map((theme, i) => (
                  <Badge key={i} className="bg-purple-100 text-purple-700 border-purple-200 text-xs">
                    {theme}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Rating Trend Chart ────────────────────────────────────────────────────────

function RatingTrendChart({ propertyId }: { propertyId: number }) {
  const trendsQuery = useQuery<RatingTrend[]>({
    queryKey: ["/api/app/reviews/trends", propertyId],
    queryFn: async () => {
      const res = await fetch(`/api/app/reviews/trends/${propertyId}`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json() as Promise<RatingTrend[]>;
    },
  });

  const trends = trendsQuery.data ?? [];

  if (trendsQuery.isLoading) return <Skeleton className="h-32 w-full" />;
  if (trends.length === 0) return (
    <div className="flex items-center justify-center h-24 border border-dashed border-gray-200 rounded-lg">
      <p className="text-sm text-gray-400">No trend data yet — sync reviews to populate.</p>
    </div>
  );

  const maxCount = Math.max(...trends.map(t => t.count), 1);

  return (
    <div className="space-y-2" data-testid={`chart-rating-trends-${propertyId}`}>
      <div className="flex items-end gap-2 h-28">
        {trends.slice(-12).map(t => {
          const heightPct = Math.round((t.count / maxCount) * 100);
          const color = t.avgRating == null
            ? "bg-gray-200"
            : t.avgRating >= 4
              ? "bg-green-400"
              : t.avgRating >= 3
                ? "bg-amber-400"
                : "bg-red-400";
          return (
            <div
              key={t.month}
              className="flex flex-col items-center flex-1 gap-1"
              title={`${t.month}: ${t.count} review${t.count !== 1 ? "s" : ""}${t.avgRating != null ? `, avg ${t.avgRating}★` : ""}`}
            >
              <span className="text-[10px] text-gray-500 leading-none">
                {t.avgRating != null ? t.avgRating.toFixed(1) : "—"}
              </span>
              <div
                className={`w-full rounded-t ${color} transition-all`}
                style={{ height: `${Math.max(heightPct, 4)}%` }}
              />
              <span className="text-[9px] text-gray-400 leading-none truncate w-full text-center">
                {t.month.slice(5)}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3 text-[10px] text-gray-500">
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-green-400" />≥4★</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-amber-400" />3-4★</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm bg-red-400" />&lt;3★</span>
      </div>
    </div>
  );
}

// ── Property Drill-Down ───────────────────────────────────────────────────────

function PropertyDrillDown({
  property,
  reviews,
  onBack,
  onFlag,
  onMemberClick,
  canEdit,
}: {
  property: Property;
  reviews: ReviewWithType[];
  onBack: () => void;
  onFlag: (t: FlagTarget) => void;
  onMemberClick: (name: string) => void;
  canEdit: boolean;
}) {
  const propertyReviews = reviews.filter(r => r.propertyId === property.id);
  const rated = propertyReviews.filter(r => r.rating != null);
  const avg = rated.length > 0 ? rated.reduce((s, r) => s + (r.rating as number), 0) / rated.length : null;
  const positive = propertyReviews.filter(r => r.sentiment === "positive").length;
  const negative = propertyReviews.filter(r => r.sentiment === "negative").length;
  const low = propertyReviews.filter(r => r.rating != null && r.rating < 3).length;

  return (
    <div className="space-y-5" data-testid={`drilldown-property-${property.id}`}>
      {/* Back + Header */}
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="mb-3 text-gray-600 -ml-2"
          data-testid="button-drilldown-back"
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
          All Properties
        </Button>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Home className="h-4.5 w-4.5 text-blue-600" />
              {property.name}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">Per-property review drill-down</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-gray-200">
          <CardContent className="pt-3 pb-3">
            <div className="text-2xl font-bold text-gray-900" data-testid={`drilldown-stat-total-${property.id}`}>{propertyReviews.length}</div>
            <div className="text-xs text-gray-500 flex items-center gap-1"><MessageSquare className="h-3 w-3" />Total</div>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="pt-3 pb-3">
            <div className="text-2xl font-bold text-amber-600" data-testid={`drilldown-stat-avg-${property.id}`}>
              {avg != null ? avg.toFixed(1) : "—"}
            </div>
            <div className="text-xs text-gray-500 flex items-center gap-1"><Star className="h-3 w-3" />Avg rating</div>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="pt-3 pb-3">
            <div className="text-2xl font-bold text-green-600" data-testid={`drilldown-stat-positive-${property.id}`}>{positive}</div>
            <div className="text-xs text-gray-500 flex items-center gap-1"><TrendingUp className="h-3 w-3" />Positive</div>
          </CardContent>
        </Card>
        <Card className="border-red-100">
          <CardContent className="pt-3 pb-3">
            <div className="text-2xl font-bold text-red-600" data-testid={`drilldown-stat-negative-${property.id}`}>{negative + low}</div>
            <div className="text-xs text-gray-500 flex items-center gap-1"><TrendingDown className="h-3 w-3" />Low / Negative</div>
          </CardContent>
        </Card>
      </div>

      {/* Rating trend chart */}
      <Card className="border-gray-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <BarChart2 className="h-4 w-4 text-blue-500" />
            Rating Trends Over Time
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <RatingTrendChart propertyId={property.id} />
        </CardContent>
      </Card>

      {/* AI Summary */}
      <SummaryPanel propertyId={property.id} propertyName={property.name} />

      {/* Reviews list */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          All Reviews ({propertyReviews.length})
        </h3>
        {propertyReviews.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center border border-dashed border-gray-200 rounded-lg" data-testid="drilldown-empty">
            <Star className="h-10 w-10 text-gray-200 mb-2" />
            <p className="text-gray-500 text-sm">No reviews for this property yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {propertyReviews.map(review => (
              <ReviewCard
                key={`${review.reviewType}-${review.id}`}
                review={review}
                onFlag={onFlag}
                onMemberClick={onMemberClick}
                canEdit={canEdit}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Stats Row (dashboard) ─────────────────────────────────────────────────────

function StatsRow({ reviews }: { reviews: ReviewWithType[] }) {
  const rated = reviews.filter(r => r.rating != null);
  const avg = rated.length > 0 ? rated.reduce((s, r) => s + (r.rating as number), 0) / rated.length : null;
  const positive = reviews.filter(r => r.sentiment === "positive").length;
  const low = reviews.filter(r => (r.rating != null && r.rating < 3) || r.sentiment === "negative").length;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
      <Card className="border-gray-200">
        <CardContent className="pt-3 pb-3">
          <div className="text-2xl font-bold text-gray-900" data-testid="stat-total-reviews">{reviews.length}</div>
          <div className="text-xs text-gray-500 flex items-center gap-1"><MessageSquare className="h-3 w-3" />Total reviews</div>
        </CardContent>
      </Card>
      <Card className="border-gray-200">
        <CardContent className="pt-3 pb-3">
          <div className="text-2xl font-bold text-amber-600" data-testid="stat-avg-rating">
            {avg != null ? avg.toFixed(1) : "—"}
          </div>
          <div className="text-xs text-gray-500 flex items-center gap-1"><Star className="h-3 w-3" />Avg rating</div>
        </CardContent>
      </Card>
      <Card className="border-gray-200">
        <CardContent className="pt-3 pb-3">
          <div className="text-2xl font-bold text-green-600" data-testid="stat-positive">{positive}</div>
          <div className="text-xs text-gray-500 flex items-center gap-1"><TrendingUp className="h-3 w-3" />Positive</div>
        </CardContent>
      </Card>
      <Card className="border-red-100">
        <CardContent className="pt-3 pb-3">
          <div className="text-2xl font-bold text-red-600" data-testid="stat-low-ratings">{low}</div>
          <div className="text-xs text-gray-500 flex items-center gap-1"><TrendingDown className="h-3 w-3" />Low / Negative</div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Suppression Resolve Dialog (Task #1496) ──────────────────────────────────

function ResolveSuppressionDialog({
  request,
  onClose,
}: {
  request: SuppressionRequestRecord | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [status, setStatus] = useState<string>(request?.status === "approved" || request?.status === "denied" ? request.status : "approved");
  const [responseNotes, setResponseNotes] = useState<string>(request?.responseNotes ?? "");
  const [hideReview, setHideReview] = useState<boolean>(request?.hideReview ?? false);

  useEffect(() => {
    if (request) {
      setStatus(request.status === "approved" || request.status === "denied" ? request.status : "approved");
      setResponseNotes(request.responseNotes ?? "");
      setHideReview(request.hideReview);
    }
  }, [request]);

  const updateMutation = useMutation({
    mutationFn: () =>
      apiRequest("PATCH", `/api/app/reviews/suppression-requests/${request!.id}`, {
        status,
        responseNotes: responseNotes || null,
        hideReview: status === "approved" ? hideReview : false,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/reviews/suppression-requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/reviews"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/reviews/stay"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/reviews/move-in"] });
      toast({ title: `Suppression marked ${status}` });
      onClose();
    },
    onError: () => toast({ title: "Could not update suppression request", variant: "destructive" }),
  });

  if (!request) return null;

  return (
    <Dialog open={!!request} onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record PadSplit Response</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <p className="text-sm text-gray-600">
            Capture the outcome PadSplit support gave you for{" "}
            <strong>{request.memberName ?? `Review #${request.reviewId}`}</strong>.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="resolve-status">Outcome</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger id="resolve-status" data-testid="select-resolve-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="denied">Denied</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="resolve-notes">Response notes (optional)</Label>
            <Textarea
              id="resolve-notes"
              value={responseNotes}
              onChange={e => setResponseNotes(e.target.value)}
              placeholder="Paste PadSplit's reply, ticket #, or any context…"
              className="h-20 text-sm resize-none"
              data-testid="textarea-resolve-notes"
            />
          </div>
          {status === "approved" && (
            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={hideReview}
                onChange={e => setHideReview(e.target.checked)}
                className="mt-0.5"
                data-testid="checkbox-resolve-hide-review"
              />
              <span>
                Hide the underlying review from the Reviews dashboard.
              </span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="button-resolve-cancel">
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => updateMutation.mutate()}
            disabled={updateMutation.isPending}
            data-testid="button-resolve-submit"
          >
            {updateMutation.isPending ? "Saving…" : "Save Response"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Manage Tab: Flag History ──────────────────────────────────────────────────

function ManageTab({ initialMemberFilter }: { initialMemberFilter: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [memberSearch, setMemberSearch] = useState(initialMemberFilter);

  const flagsQuery = useQuery<FlagRecord[]>({
    queryKey: ["/api/app/reviews/flags"],
    queryFn: async () => {
      const res = await fetch("/api/app/reviews/flags", { credentials: "include" });
      if (!res.ok) return [];
      return res.json() as Promise<FlagRecord[]>;
    },
  });

  const suppressionRequestsQuery = useQuery<SuppressionRequestRecord[]>({
    queryKey: ["/api/app/reviews/suppression-requests"],
    queryFn: async () => {
      const res = await fetch("/api/app/reviews/suppression-requests", { credentials: "include" });
      if (!res.ok) return [];
      return res.json() as Promise<SuppressionRequestRecord[]>;
    },
  });

  const requestsQuery = useQuery<ReviewRequestRecord[]>({
    queryKey: ["/api/app/reviews/requests"],
    queryFn: async () => {
      const res = await fetch("/api/app/reviews/requests", { credentials: "include" });
      if (!res.ok) return [];
      return res.json() as Promise<ReviewRequestRecord[]>;
    },
  });

  const membersQuery = useQuery<{ id: number; name: string | null }[]>({
    queryKey: ["/api/app/members"],
  });

  const requestMutation = useMutation({
    mutationFn: (memberId: number) =>
      apiRequest("POST", `/api/app/reviews/request/${memberId}`).then(r => r.json() as Promise<{ message: string }>),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/reviews/requests"] });
      toast({ title: "Review request noted", description: data.message });
    },
    onError: () => toast({ title: "Could not send request", variant: "destructive" }),
  });

  const flags = flagsQuery.data ?? [];
  const requests = requestsQuery.data ?? [];
  const suppressionRequests = suppressionRequestsQuery.data ?? [];
  const [resolveTarget, setResolveTarget] = useState<SuppressionRequestRecord | null>(null);
  const { canEdit } = usePermissions();
  const canResolve = canEdit("maintenance");

  const matchedMember = membersQuery.data?.find(m =>
    memberSearch.trim().length > 0 &&
    m.name?.toLowerCase().includes(memberSearch.toLowerCase().trim())
  );

  return (
    <div className="space-y-8">
      {/* Review Request Panel */}
      <div>
        <h2 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-blue-500" />
          Request a Review
        </h2>
        <Card className="border-blue-100">
          <CardContent className="pt-4 space-y-4">
            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <Info className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800">
                PadSplit sends CSAT emails to members on their own biweekly schedule. Logging a request here records your
                intent for tracking — it does not directly trigger a PadSplit email.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <Input
                  value={memberSearch}
                  onChange={e => setMemberSearch(e.target.value)}
                  placeholder="Search member name…"
                  className="pl-8 h-9 text-sm"
                  data-testid="input-manage-member-search"
                />
              </div>
              <Button
                size="sm"
                disabled={!matchedMember || requestMutation.isPending}
                onClick={() => matchedMember && requestMutation.mutate(matchedMember.id)}
                data-testid="button-request-review"
              >
                {requestMutation.isPending ? "Noting…" : "Note Request"}
              </Button>
            </div>
            {memberSearch.trim().length > 0 && (
              matchedMember ? (
                <p className="text-xs text-green-700 flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Found: <strong>{matchedMember.name}</strong>
                </p>
              ) : (
                <p className="text-xs text-gray-400">No member matches "{memberSearch}"</p>
              )
            )}
          </CardContent>
        </Card>

        {/* Request History */}
        {requestsQuery.isLoading ? (
          <div className="mt-3 space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : requests.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-medium text-gray-500 mb-2">Request History ({requests.length})</p>
            <div className="space-y-2">
              {requests.map(r => (
                <div
                  key={r.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-gray-100 bg-gray-50 text-sm"
                  data-testid={`card-review-request-${r.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <User className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <span className="font-medium text-gray-800 truncate">{r.memberName ?? `Member #${r.memberId}`}</span>
                    <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs shrink-0">{r.status}</Badge>
                  </div>
                  <span className="text-xs text-gray-400 shrink-0">
                    {new Date(r.requestedAt).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Formal Suppression Requests (Task #1477) */}
      <div>
        <h2 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Flag className="h-4 w-4 text-red-500" />
          Formal Suppression Requests
          <span className="text-xs font-normal text-gray-400">({suppressionRequests.length})</span>
        </h2>
        {suppressionRequestsQuery.isLoading ? (
          <div className="space-y-2">
            {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        ) : suppressionRequests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center border border-dashed rounded-xl">
            <Flag className="h-8 w-8 text-gray-200 mb-2" />
            <p className="text-gray-400 text-sm">No suppression requests yet</p>
            <p className="text-gray-400 text-xs mt-1">
              Open a low-rated review and choose <strong>Request Suppression</strong> to file one.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {suppressionRequests.map(s => {
              const created = s.createdAt ? new Date(s.createdAt).toLocaleDateString() : "—";
              const statusBadgeClass =
                s.status === "approved" ? "bg-green-100 text-green-700 border-green-200"
                : s.status === "denied" ? "bg-red-100 text-red-700 border-red-200"
                : s.status === "sent" ? "bg-blue-50 text-blue-700 border-blue-200"
                : "bg-gray-100 text-gray-700 border-gray-200";
              return (
                <Card key={s.id} className="border border-red-100 bg-red-50/20" data-testid={`card-suppression-request-${s.id}`}>
                  <CardContent className="pt-3 pb-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
                            {s.reviewType === "stay" ? "Stay Review" : "Move-In Review"}
                          </Badge>
                          <Badge className="bg-orange-50 text-orange-700 border-orange-200 text-xs">
                            {suppressionReasonLabel(s.reason)}
                          </Badge>
                          <Badge className={`${statusBadgeClass} text-xs capitalize`} data-testid={`badge-suppression-status-${s.id}`}>
                            {s.status}
                          </Badge>
                          {s.emailDraftSent && (
                            <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                              Email draft sent
                            </Badge>
                          )}
                          {s.hideReview && s.status === "approved" && (
                            <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-xs">
                              Review hidden
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {s.memberName ?? "Unknown member"} · Review #{s.reviewId}
                          {s.padsplitReviewId ? ` · PadSplit ID ${s.padsplitReviewId}` : ""}
                        </p>
                        {s.notes && (
                          <p className="text-xs text-gray-600 mt-1">Notes: {s.notes}</p>
                        )}
                        {s.responseNotes && (
                          <p className="text-xs text-gray-700 mt-1 p-2 bg-white/70 border border-gray-100 rounded" data-testid={`text-suppression-response-${s.id}`}>
                            <span className="font-medium">PadSplit response:</span> {s.responseNotes}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          Filed on {created}
                          {s.emailSentAt ? ` · Email sent ${new Date(s.emailSentAt).toLocaleDateString()}` : ""}
                          {s.respondedAt ? ` · Resolved ${new Date(s.respondedAt).toLocaleDateString()}` : ""}
                        </p>
                      </div>
                      {canResolve && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs shrink-0"
                          onClick={() => setResolveTarget(s)}
                          data-testid={`button-resolve-suppression-${s.id}`}
                        >
                          {s.status === "approved" || s.status === "denied" ? "Edit Response" : "Record Response"}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
        {suppressionRequests.length > 0 && (
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
            <Info className="h-3 w-3 shrink-0" />
            PadSplit support reviews suppression requests directly. Forward your draft email to complete the process.
          </p>
        )}
      </div>

      {/* Legacy flag history (notification-backed, kept for prior records) */}
      <div>
        <h2 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Flag className="h-4 w-4 text-red-500" />
          Legacy Flag History
          <span className="text-xs font-normal text-gray-400">({flags.length})</span>
        </h2>
        {flagsQuery.isLoading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        ) : flags.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center border border-dashed rounded-xl">
            <Flag className="h-8 w-8 text-gray-200 mb-2" />
            <p className="text-gray-400 text-sm">No suppression requests yet</p>
            <p className="text-gray-400 text-xs mt-1">
              Flag a low-rated or negative review from the Reviews tab to log a request here.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {flags.map(f => {
              const meta = f.metadata;
              const flagDate = meta.flaggedAt ? new Date(meta.flaggedAt).toLocaleDateString() : "—";
              return (
                <Card key={f.id} className="border border-red-100 bg-red-50/20" data-testid={`card-flag-${f.id}`}>
                  <CardContent className="pt-3 pb-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
                            {meta.reviewType === "stay" ? "Stay Review" : "Move-In Review"}
                          </Badge>
                          <Badge className="bg-orange-50 text-orange-700 border-orange-200 text-xs">
                            {meta.reason}
                          </Badge>
                          <span className="text-xs text-gray-400">Review ID #{meta.reviewId}</span>
                        </div>
                        {meta.notes && (
                          <p className="text-xs text-gray-600 mt-1">Notes: {meta.notes}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          Flagged on {flagDate} · Contact PadSplit support to complete suppression.
                        </p>
                      </div>
                      <Link href={`/app/reviews?reviewId=${meta.reviewId}`}>
                        <a
                          className="flex items-center gap-1 text-xs text-blue-600 hover:underline shrink-0"
                          data-testid={`link-flag-review-${f.id}`}
                        >
                          View review <ChevronRight className="h-3 w-3" />
                        </a>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
        {flags.length > 0 && (
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
            <Info className="h-3 w-3 shrink-0" />
            To complete a suppression, contact PadSplit support and reference the Review ID above.
          </p>
        )}
      </div>

      <ResolveSuppressionDialog
        request={resolveTarget}
        onClose={() => setResolveTarget(null)}
      />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AppReviews() {
  usePageTitle("Reviews");
  return (
    <AppLayout>
      <BetaAccessGate featureKey="reviews" featureName="Reviews" loadingFallback={<Skeleton className="h-32 m-6" />}>
        <ReviewsContent />
      </BetaAccessGate>
    </AppLayout>
  );
}

function ReviewsContent() {
  const queryClient = useQueryClient();
  const { canEdit } = usePermissions();
  const canSync = canEdit("maintenance");
  const { toast } = useToast();
  const [location] = useLocation();

  const searchParams = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : ""
  );
  const urlMemberParam = searchParams.get("member") ?? "";
  const urlPropertyIdParam = searchParams.get("propertyId") ?? "";

  const [mainSectionParam, setMainSection] = useTabQueryParam("tab", "reviews", {
    tabs: ["reviews", "manage"],
  });
  const mainSection: MainSection = mainSectionParam as MainSection;
  const [tabParam, setTab] = useTabQueryParam("reviewType", "all", {
    tabs: ["all", "stay", "move_in"],
  });
  const tab: TabType = tabParam as TabType;
  const [search, setSearch] = useListState("reviews:search", "");
  const [filterSentiment, setFilterSentimentState] = useState<SentimentFilter>(
    () => (localStorage.getItem("pp:reviews-sentiment-filter") as SentimentFilter) || "all",
  );
  const setFilterSentiment = (next: SentimentFilter) => {
    localStorage.setItem("pp:reviews-sentiment-filter", next);
    setFilterSentimentState(next);
  };
  const [filterPropertyId, setFilterPropertyId] = useListState<string>("reviews:property",
    urlPropertyIdParam && /^\d+$/.test(urlPropertyIdParam) ? urlPropertyIdParam : "all"
  );

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const pid = p.get("propertyId");
    if (pid && /^\d+$/.test(pid)) setFilterPropertyId(pid);
  }, [location]);
  const [filterDateRange, setFilterDateRangeState] = useState<DateRangeFilter>(
    () => (localStorage.getItem("pp:reviews-date-range-filter") as DateRangeFilter) || "all",
  );
  const setFilterDateRange = (next: DateRangeFilter) => {
    localStorage.setItem("pp:reviews-date-range-filter", next);
    setFilterDateRangeState(next);
  };
  const [filterRating, setFilterRatingState] = useState<RatingFilter>(
    () => (localStorage.getItem("pp:reviews-rating-filter") as RatingFilter) || "all",
  );
  const setFilterRating = (next: RatingFilter) => {
    localStorage.setItem("pp:reviews-rating-filter", next);
    setFilterRatingState(next);
  };
  const [memberDrilldown, setMemberDrilldown] = useState<string | null>(null);
  const [drilldownPropertyId, setDrilldownPropertyId] = useState<number | null>(null);
  const [flagTarget, setFlagTarget] = useState<FlagTarget | null>(null);

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const reviewsQuery = useQuery<ReviewWithType[]>({
    queryKey: ["/api/app/reviews"],
  });

  const reviews = reviewsQuery.data ?? [];
  const properties = propertiesQuery.data ?? [];

  const cutoff = dateRangeCutoff(filterDateRange);

  const filteredReviews = reviews.filter((r) => {
    if (tab === "stay" && r.reviewType !== "stay") return false;
    if (tab === "move_in" && r.reviewType !== "move_in") return false;
    if (filterSentiment !== "all" && r.sentiment !== filterSentiment) return false;
    if (filterPropertyId !== "all" && String(r.propertyId) !== filterPropertyId) return false;
    if (filterRating !== "all" && String(r.rating) !== filterRating) return false;
    if (cutoff && r.reviewDate && new Date(r.reviewDate) < cutoff) return false;
    if (memberDrilldown && r.memberName !== memberDrilldown) return false;
    if (search) {
      const q = search.toLowerCase();
      const matches =
        String(r.memberName ?? "").toLowerCase().includes(q) ||
        String(r.reviewText ?? "").toLowerCase().includes(q);
      if (!matches) return false;
    }
    return true;
  });

  const drilldownProperty = drilldownPropertyId != null
    ? properties.find(p => p.id === drilldownPropertyId) ?? null
    : null;

  return (
    <>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <ViewOnlyBanner area="maintenance" />

        {/* Flag dialog */}
        <FlagReviewDialog target={flagTarget} onClose={() => setFlagTarget(null)} />

        {/* Top-level section: Reviews | Manage */}
        {!drilldownProperty && (
          <div className="flex items-center gap-1 mb-5 border-b border-gray-200">
            <button
              onClick={() => setMainSection("reviews")}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                mainSection === "reviews"
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
              data-testid="section-tab-reviews"
            >
              <Star className="h-3.5 w-3.5" />
              Reviews
            </button>
            <button
              onClick={() => setMainSection("manage")}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                mainSection === "manage"
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
              data-testid="section-tab-manage"
              data-tour="reviews-notifications"
            >
              <Settings2 className="h-3.5 w-3.5" />
              Manage
            </button>
          </div>
        )}

        {/* Manage section */}
        {mainSection === "manage" && !drilldownProperty && (
          <ManageTab initialMemberFilter={urlMemberParam} />
        )}

        {/* Reviews section + Property Drill-Down */}
        {(mainSection === "reviews" || drilldownProperty) && (
        <>
        {/* Property Drill-Down view */}
        {drilldownProperty ? (
          <PropertyDrillDown
            property={drilldownProperty}
            reviews={reviews}
            onBack={() => setDrilldownPropertyId(null)}
            onFlag={setFlagTarget}
            onMemberClick={name => { setMemberDrilldown(name); setDrilldownPropertyId(null); }}
            canEdit={canSync}
          />
        ) : (
          <>
            {/* Header */}
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2" data-tour="reviews-heading">
                  <Star className="h-5 w-5 text-amber-500" />
                  PadSplit Reviews
                </h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  Stay (CSAT) and move-in reviews from your PadSplit dashboard.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <ExplainerVideoButton pageKey="reviews" />
                <GuideButton pageKey="reviews" />
                {filterPropertyId !== "all" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDrilldownPropertyId(parseInt(filterPropertyId, 10))}
                    data-testid="button-open-drilldown"
                  >
                    <BarChart2 className="h-3.5 w-3.5 mr-1 text-blue-600" />
                    Property View
                  </Button>
                )}
              </div>
            </div>

            <GuideNudgeBanner pageKey="reviews" />

            {/* Stats */}
            <div data-tour="reviews-summary">
              {reviewsQuery.isLoading ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : (
                <StatsRow reviews={filteredReviews} />
              )}
            </div>

            {/* Property cards for drill-down selection */}
            {properties.length > 0 && filterPropertyId === "all" && (
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2 font-medium">Jump to property</p>
                <div className="flex flex-wrap gap-2">
                  {properties.map(p => {
                    const propReviews = reviews.filter(r => r.propertyId === p.id);
                    const propRated = propReviews.filter(r => r.rating != null);
                    const propAvg = propRated.length > 0
                      ? propRated.reduce((s, r) => s + (r.rating as number), 0) / propRated.length
                      : null;
                    return (
                      <button
                        key={p.id}
                        onClick={() => setDrilldownPropertyId(p.id)}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:border-blue-400 hover:bg-blue-50/30 text-sm text-gray-700 transition-colors"
                        data-testid={`button-property-drilldown-${p.id}`}
                      >
                        <Home className="h-3.5 w-3.5 text-gray-400" />
                        <span className="font-medium">{p.name}</span>
                        {propAvg != null && (
                          <span className="flex items-center gap-0.5 text-amber-600 text-xs">
                            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                            {propAvg.toFixed(1)}
                          </span>
                        )}
                        <span className="text-gray-400 text-xs">({propReviews.length})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Member drill-down banner */}
            {memberDrilldown && (
              <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <User className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                <span className="text-blue-800 font-medium">Showing all reviews for: {memberDrilldown}</span>
                <button
                  onClick={() => setMemberDrilldown(null)}
                  className="ml-auto text-blue-600 hover:text-blue-900 text-xs underline"
                  data-testid="button-clear-member-drilldown"
                >
                  Clear
                </button>
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-4" data-tour="reviews-filters">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search member or review text…"
                  className="pl-8 h-8 text-sm"
                  data-testid="input-search-reviews"
                />
              </div>

              <SearchableSelect
                value={filterPropertyId}
                onValueChange={setFilterPropertyId}
                placeholder="All properties"
                searchPlaceholder="Search properties..."
                emptyText="No properties match."
                testId="select-filter-property"
                className="h-8 text-xs w-44"
                leadingIcon={<Home className="h-3 w-3 mr-1.5 text-gray-400" />}
                options={[
                  { value: "all", label: "All properties" },
                  ...properties.map(p => ({ value: String(p.id), label: p.name })),
                ]}
              />

              <Select value={filterSentiment} onValueChange={v => setFilterSentiment(v as SentimentFilter)}>
                <SelectTrigger className="h-8 text-xs w-36" data-testid="select-filter-sentiment">
                  <Filter className="h-3 w-3 mr-1.5 text-gray-400" />
                  <SelectValue placeholder="Sentiment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sentiments</SelectItem>
                  <SelectItem value="positive">Positive</SelectItem>
                  <SelectItem value="neutral">Neutral</SelectItem>
                  <SelectItem value="negative">Negative</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterRating} onValueChange={v => setFilterRating(v as RatingFilter)}>
                <SelectTrigger className="h-8 text-xs w-28" data-testid="select-filter-rating">
                  <Star className="h-3 w-3 mr-1.5 text-gray-400" />
                  <SelectValue placeholder="Rating" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any rating</SelectItem>
                  {([5, 4, 3, 2, 1] as const).map(n => (
                    <SelectItem key={n} value={String(n)}>{n}★</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterDateRange} onValueChange={v => setFilterDateRange(v as DateRangeFilter)}>
                <SelectTrigger className="h-8 text-xs w-32" data-testid="select-filter-date">
                  <SelectValue placeholder="Date range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All time</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tabs */}
            <Tabs value={tab} onValueChange={setTab} data-tour="reviews-list">
              <TabsList className="h-8 mb-4">
                <TabsTrigger value="all" className="text-xs" data-testid="tab-all-reviews">
                  All ({reviews.length})
                </TabsTrigger>
                <TabsTrigger value="stay" className="text-xs" data-testid="tab-stay-reviews">
                  Stay ({reviews.filter(r => r.reviewType === "stay").length})
                </TabsTrigger>
                <TabsTrigger value="move_in" className="text-xs" data-testid="tab-movein-reviews">
                  Move-In ({reviews.filter(r => r.reviewType === "move_in").length})
                </TabsTrigger>
                <TabsTrigger value="pending" className="text-xs" data-testid="tab-pending-reviews">
                  Pending Requests
                </TabsTrigger>
              </TabsList>

              <TabsContent value="pending">
                <PendingReviewRequests canEdit={canSync} />
              </TabsContent>

              {(["all", "stay", "move_in"] as const).map(t => (
                <TabsContent key={t} value={t}>
                  {reviewsQuery.isLoading ? (
                    <div className="space-y-3">
                      {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-28 w-full" />)}
                    </div>
                  ) : filteredReviews.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-reviews">
                      <Star className="h-12 w-12 text-gray-200 mb-3" />
                      <p className="text-gray-500 font-medium">
                        {reviews.length === 0 ? "No reviews have synced from PadSplit yet" : "No reviews found"}
                      </p>
                      <p className="text-gray-400 text-sm mt-1 max-w-md">
                        {reviews.length === 0
                          ? "Reviews sync automatically from your PadSplit account."
                          : "Try adjusting your filters."}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredReviews.map(review => (
                        <ReviewCard
                          key={`${review.reviewType}-${review.id}`}
                          review={review}
                          onFlag={setFlagTarget}
                          onMemberClick={name => { setMemberDrilldown(name); }}
                          canEdit={canSync}
                        />
                      ))}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </>
        )}
        </>
        )}
      </div>
    </>
  );
}
